import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Upload, Loader2, ShieldCheck, FileText, PartyPopper, Clock, Send, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VideoRecorder from "./VideoRecorder";
import { useTranslation, Trans } from "react-i18next";

export default function UploadLinkPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [recDoc, setRecDoc] = useState(null);
  const fileRef = useRef(null);
  const targetDoc = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/upload-link/${token}`);
      setData(r.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pick = (docId, video) => {
    targetDoc.current = docId;
    if (fileRef.current) fileRef.current.accept = video ? "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v" : ".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,application/pdf,image/*";
    fileRef.current?.click();
  };

  const uploadFile = async (docId, file) => {
    setUploading(docId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/upload-link/${token}/documents/${docId}`, fd);
      setData((d) => ({ ...d, documents: res.data.documents, status: res.data.status }));
      toast.success(t("client.docUploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("client.uploadFailedRetry"));
    } finally {
      setUploading(null);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/upload-link/${token}/submit`);
      setData((d) => ({ ...d, submitted: true, status: res.data.status }));
      toast.success(t("client.ulSubmittedToast"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("client.ulSubmitErr"));
    } finally {
      setSubmitting(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const docId = targetDoc.current;
    const doc = (data?.documents || []).find((x) => x.id === docId);
    const isVideo = doc?.kind === "video";
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (isVideo) {
      if (!["mp4", "webm", "mov", "m4v"].includes(ext)) {
        toast.error(t("client.badVideoType"));
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error(t("client.videoTooLarge"));
        return;
      }
    } else {
      const ALLOWED = ["pdf", "jpg", "jpeg", "png", "webp", "heic", "doc", "docx"];
      if (!ALLOWED.includes(ext)) {
        toast.error(t("client.badFileType"));
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error(t("client.fileTooLarge"));
        return;
      }
    }
    await uploadFile(docId, file);
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#F7F5EF]"><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>;

  const org = data?.org || { name: "UNICORP", logo: "" };
  const expired = error || data?.expired;

  return (
    <div className="min-h-screen bg-[#F7F5EF] grain" data-testid="upload-link-page">
      <input type="file" ref={fileRef} onChange={onFile} accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,application/pdf,image/*" className="hidden" data-testid="ul-file-input" />

      {/* Header */}
      <header className="border-b border-[#e7e2d6] bg-white/70 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-3">
          {org.logo
            ? <img src={org.logo} alt={org.name} className="h-8 w-auto" data-testid="ul-logo" />
            : <span className="font-heading text-xl tracking-tight text-[#071633]">{org.name}</span>}
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#66758F]"><ShieldCheck className="h-3.5 w-3.5 text-[#23488F]" /> {t("client.ulSecure")}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-10">
        {expired ? (
          <div className="rounded-2xl border border-[#e7e2d6] bg-white p-8 text-center" data-testid="ul-expired">
            <span className="grid place-items-center h-12 w-12 rounded-full bg-amber-50 text-amber-600 mx-auto"><Clock className="h-6 w-6" /></span>
            <h1 className="mt-4 font-heading text-2xl text-[#071633]">{t("client.ulExpiredTitle")}</h1>
            <p className="mt-2 text-[#66758F]">{t("client.ulExpiredBody")}</p>
            <a href="https://uniincorp.com/contact" className="mt-5 inline-flex rounded-sm bg-[#23488F] text-white px-5 py-2.5 text-sm hover:bg-[#1b3a75]" data-testid="ul-contact-btn">{t("client.ulContact")}</a>
          </div>
        ) : data.submitted ? (
          <div className="rounded-2xl border border-green-200 bg-white p-8 text-center" data-testid="ul-submitted">
            <span className="grid place-items-center h-14 w-14 rounded-full bg-green-100 text-green-600 mx-auto"><PartyPopper className="h-7 w-7" /></span>
            <h1 className="mt-4 font-heading text-2xl text-[#071633]">{t("client.ulSubmittedTitle")}</h1>
            <p className="mt-2 text-[#66758F]">{data.client_name ? t("client.ulThanksName", { name: data.client_name }) : t("client.ulThanks")}</p>
          </div>
        ) : (
          <>
            <div data-testid="ul-intro">
              <p className="text-xs uppercase tracking-[0.2em] text-orange-700">{t("client.docRequest")}</p>
              <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tight text-[#071633]">
                {data.client_name ? t("client.ulHiName", { name: data.client_name }) : t("client.ulHello")}
              </h1>
              <p className="mt-2 text-[#66758F]">
                {data.message || t("client.ulDefaultMsg")}
              </p>
            </div>

            {data.status === "completed" && (
              <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4 flex items-center gap-3" data-testid="ul-complete-banner">
                <PartyPopper className="h-5 w-5 text-green-600 shrink-0" />
                <p className="text-sm text-green-800"><Trans i18nKey="client.ulAllUploaded" components={{ b: <strong /> }} /></p>
              </div>
            )}

            <div className="mt-6 space-y-3">
              {(data.documents || []).map((d) => {
                const done = d.uploaded;
                const busy = uploading === d.id;
                return (
                  <div key={d.id} className={`rounded-xl border p-4 ${done ? "border-green-200 bg-green-50/60" : "border-[#e7e2d6] bg-white"}`} data-testid={`ul-doc-${d.id}`}>
                    {d.kind === "video" && d.instructions ? (
                      <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid={`ul-video-instructions-${d.id}`}>
                        <p className="text-[11px] uppercase tracking-wider text-blue-700 mb-1">{t("client.videoInstructions")}</p>
                        {d.instructions}
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`grid place-items-center h-9 w-9 rounded-sm shrink-0 ${done ? "bg-green-100 text-green-600" : "bg-[#eef1f7] text-[#23488F]"}`}>
                          {done ? <CheckCircle2 className="h-5 w-5" data-testid={`ul-tick-${d.id}`} /> : <FileText className="h-4 w-4" />}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#071633] truncate">{d.name}</p>
                          <p className="text-xs text-[#66758F] truncate">{done ? (d.filename || t("client.uploaded")) : t("client.awaitingUpload")}</p>
                        </div>
                      </div>
                      {d.kind === "video" ? (
                        <div className="shrink-0 flex items-center gap-2 flex-wrap">
                          <button onClick={() => setRecDoc(d.id)} disabled={busy} className="inline-flex items-center rounded-sm bg-[#23488F] text-white px-3 py-2 text-sm hover:bg-[#1b3a75] disabled:opacity-50" data-testid={`ul-record-${d.id}`}><Video className="h-4 w-4 mr-1.5" /> {t("client.recordNow")}</button>
                          <button onClick={() => pick(d.id, true)} disabled={busy} className={`inline-flex items-center rounded-sm px-3.5 py-2 text-sm transition-colors ${done ? "border border-[#d7d0c0] bg-white text-[#66758F] hover:bg-[#f3f0e8]" : "bg-orange-500 text-white hover:bg-orange-600"}`} data-testid={`ul-video-upload-${d.id}`}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (done ? t("client.replace") : <><Upload className="h-4 w-4 mr-1.5" /> {t("client.uploadVideo")}</>)}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => pick(d.id)}
                          disabled={busy}
                          className={`shrink-0 inline-flex items-center rounded-sm px-3.5 py-2 text-sm transition-colors ${done ? "border border-[#d7d0c0] bg-white text-[#66758F] hover:bg-[#f3f0e8]" : "bg-orange-500 text-white hover:bg-orange-600"}`}
                          data-testid={`ul-upload-${d.id}`}>
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (done ? t("client.replace") : <><Upload className="h-4 w-4 mr-1.5" /> {t("client.upload")}</>)}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {(() => {
              const uploaded = (data.documents || []).filter((d) => d.uploaded).length;
              const total = (data.documents || []).length;
              return (
                <div className="mt-6" data-testid="ul-submit-area">
                  <button
                    onClick={submit}
                    disabled={submitting || uploaded === 0}
                    className="w-full inline-flex items-center justify-center rounded-sm bg-[#23488F] text-white px-5 py-3 text-sm font-medium hover:bg-[#1b3a75] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="ul-submit-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> {t("client.ulSubmit")}</>}
                  </button>
                  <p className="mt-2 text-center text-xs text-[#9aa3b2]">
                    {uploaded === 0
                      ? t("client.ulNeedOne")
                      : t("client.ulProgress", { n: uploaded, total })}
                  </p>
                </div>
              );
            })()}

            <p className="mt-8 text-center text-xs text-[#9aa3b2]">
              {t("client.ulFooter")}
            </p>
          </>
        )}
      </main>
      <VideoRecorder open={!!recDoc} uploading={uploading === recDoc} onClose={() => setRecDoc(null)} onConfirm={(f) => { const id = recDoc; setRecDoc(null); uploadFile(id, f); }} />
    </div>
  );
}
